
"use client"

import { useState, useEffect } from "react"
import { Modal, Button, Spinner, Form, Row, Col, Container, Badge } from "react-bootstrap"
import { ArrowLeft, User, Phone, Search, X } from "lucide-react"
import { toast } from "react-toastify"
import StudentPopupView from "./StudentPopView.jsx"
import { getAppliedStudentsForJob } from "../../../../services/placementServices.js"
import { getAppliedStudents as getAppliedStudentsLocal } from "../../../../services/AppliedStudentsService.js"

import "../../placement/placementpopup/PlacementPopup.css"

const AppliedStudentsView = ({
  jobProfile,
  isVisible,
  onClose,
  onCloseAll, // Add this prop to handle closing all modals
  users,
  contacts,
  courses,
  departments = [],
  branches = [],
  batches = [],
  jobProfiles = [],
}) => {
  const [appliedStudents, setAppliedStudents] = useState([])
  const [filteredStudents, setFilteredStudents] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [showStudentPopup, setShowStudentPopup] = useState(false)
  const [selectedStudentForPopup, setSelectedStudentForPopup] = useState(null)
  const [allApplications, setAllApplications] = useState([])

  const getCourseNameById = (courseId) => {
    if (!courseId || !courses.length) return courseId || "N/A"
    const course = courses.find((c) => c.id === Number.parseInt(courseId) || c.id === courseId)
    return course ? course.courseName || course.name : courseId
  }

  const getDepartmentNameById = (departmentId) => {
    if (!departmentId || !departments.length) return departmentId || "N/A"
    const department = departments.find((d) => d.id === Number.parseInt(departmentId) || d.id === departmentId)
    return department ? department.departmentName || department.name : departmentId
  }

  const getBranchNameById = (branchId) => {
    if (!branchId || !branches.length) return branchId || "N/A"
    const branch = branches.find((b) => b.id === Number.parseInt(branchId) || b.id === branchId)
    return branch ? branch.branchName || branch.name : branchId
  }

  useEffect(() => {
    if (isVisible && jobProfile) {
      fetchAppliedStudents()
    }
  }, [isVisible, jobProfile])

  useEffect(() => {
    const trimmed = typeof searchTerm === "string" ? searchTerm.trim() : "";
    if (!trimmed) {
      setFilteredStudents(appliedStudents)
    } else {
      const lower = trimmed.toLowerCase();
      const filtered = appliedStudents.filter(
        (student) =>
          student.studentName?.toLowerCase().includes(lower) ||
          student.studentEmail?.toLowerCase().includes(lower) ||
          student.studentPhoneNumber?.includes(trimmed) ||
          getDepartmentNameById(student.department)?.toLowerCase().includes(lower) ||
          getBranchNameById(student.selectBranch)?.toLowerCase().includes(lower),
      )
      setFilteredStudents(filtered)
    }
  }, [searchTerm, appliedStudents])

  const fetchAppliedStudents = async () => {
    if (!jobProfile?.id) return

    setLoading(true)
    try {
      const response = await getAppliedStudentsForJob(jobProfile.id)

      if (response?.data) {
        setAllApplications(response.data)
        const studentsForThisJob = response.data;
        
        // console.log(`Found ${studentsForThisJob.length} students who applied for job: ${jobProfile.jobName}`)
        setAppliedStudents(studentsForThisJob)
        setFilteredStudents(studentsForThisJob)
      } else {
        setAppliedStudents([])
        setFilteredStudents([])
        setAllApplications([])
      }
    } catch (error) {
      console.error("Error fetching applied students:", error)
      toast.error("Failed to load applied students: " + error.message)
      setAppliedStudents([])
      setFilteredStudents([])
      setAllApplications([])
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setSearchTerm("")
    setAppliedStudents([])
    setFilteredStudents([])
    onClose()
  }

  // Function to handle closing all modals
  const handleCloseAll = () => {
    setSearchTerm("")
    setAppliedStudents([])
    setFilteredStudents([])
    setShowStudentPopup(false)
    setSelectedStudentForPopup(null)
    
    // Close all modals in the chain
    if (onCloseAll) {
      onCloseAll()
    } else {
      onClose()
    }
  }

  const handleEmail = (email) => {
    if (email) {
      window.location.href = `mailto:${email}`
    }
  }

  const handleCall = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const handleViewStudent = (student) => {
    if (!student) return
    
    const sid = student.studentId;
    const studentId = (Array.isArray(sid) && sid.length > 0) ? (sid[0]?.id || sid[0]) : (sid?.id || sid || student.id);

    const jobsForStudent = (allApplications || [])
      .filter((app) => {
        const appSid = app.studentId;
        const appStudentId = (Array.isArray(appSid) && appSid.length > 0) ? (appSid[0]?.id || appSid[0]) : (appSid?.id || appSid || app.id);
        return appStudentId === studentId;
      })
      .map((app) => (jobProfiles || []).find((j) => j?.id === app.jobProfileId))
      .filter(Boolean)

    const enrichedStudent = {
      ...student,
      appliedJobs: jobsForStudent,
    }

    setSelectedStudentForPopup(enrichedStudent)
    setShowStudentPopup(true)
  }

  if (!isVisible || !jobProfile) return null

  return (
    <Modal 
      show={isVisible} 
      onHide={handleClose} 
      size="xl" 
      centered 
      className="student-modal" 
      backdrop="static"
      dialogClassName="fixed-size-modal"
    >
      <div className="modal-container" style={{ padding: "20px", minHeight: "600px", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <Modal.Header className="student-modal-header" style={{ flexShrink: 0, position: "relative" }}>
          <Button
            variant="link"
            onClick={handleClose}
            style={{
              position: "absolute",
              top: "10px",
              right: "15px",
              zIndex: 1060,
              color: "#6c757d",
              textDecoration: "none",
              border: "none",
              background: "transparent",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              transition: "all 0.2s ease",
            }}
            className="modal-close-x-btn"
            title="Close"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f8f9fa";
              e.currentTarget.style.color = "#212529";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#6c757d";
            }}
          >
            <X size={20} />
          </Button>
          <div className="header-content" style={{ paddingRight: "40px" }}>
            <div className="left-section">
              <Button variant="link" onClick={handleClose} className="close-btn">
                <ArrowLeft size={20} />
              </Button>
              <div className="student-info">
                <div className="student-avatar">JS</div>
               <Container fluid className="mb-4">
  <Row>
    <Col>
      <h3 className="mb-3 fw-semibold text-dark">Students Applied for</h3>
      <div className="d-flex flex-wrap align-items-center gap-3">
        <Badge bg="primary" className="fs-6 fw-normal px-3 py-2">
          {jobProfile.jobName}
        </Badge>
        <span className="text-secondary fs-5">|</span>
        <span className="text-dark fs-6">
          <strong>Company:</strong> {jobProfile.organizationName}
        </span>
        <span className="text-secondary fs-5">|</span>
        <span className="text-dark fs-6">
          <strong>Location:</strong> {jobProfile.jobLocation}
        </span>
      </div>
    </Col>
  </Row>
</Container>
              </div>
            </div>
            <div className="action-buttons">
              <span className="badge bg-primary">
                {filteredStudents.length} Student{filteredStudents.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="student-modal-body" style={{ flexGrow: 1, overflow: "auto", minHeight: "400px" }}>
          <div className="full-width-content">
            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "300px",
                }}
              >
                <Spinner
                  animation="border"
                  style={{ color: "#f97316", width: "2.5rem", height: "2.5rem", borderWidth: "3px" }}
                />
                <p style={{ marginTop: "16px", color: "#6c757d" }}>Loading applied students...</p>
              </div>
            ) : (
              <>
                {appliedStudents.length > 0 && (
                  <div className="mb-4 p-3 bg-light rounded">
                    <Row>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Search Students</Form.Label>
                          <div className="position-relative">
                            <Search
                              size={18}
                              className="position-absolute"
                              style={{ top: "50%", left: "12px", transform: "translateY(-50%)", color: "#6c757d" }}
                            />
                            <Form.Control
                              type="text"
                              placeholder="Search students by name, email, phone..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              style={{ paddingLeft: "40px" }}
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={6} className="d-flex align-items-end">
                        {searchTerm && (
                          <div className="d-flex align-items-center">
                            <small className="text-muted me-2">
                              Showing {filteredStudents.length} of {appliedStudents.length} students
                            </small>
                            <Button variant="link" size="sm" onClick={() => setSearchTerm("")} className="p-0">
                              Clear
                            </Button>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                )}

                {filteredStudents.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover table-striped table-bordered table-sm">
                      <thead className="table-primary">
                        <tr>
                          <th>Student</th>
                          <th>Contact</th>
                          <th>Department</th>
                          <th>Branch</th>
                          <th>Technologies</th>
                          <th>Experience</th>
                          <th>Applied Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map((student, index) => (
                          <tr key={student.id || index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div
                                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2"
                                  style={{ width: "32px", height: "32px", fontSize: "12px" }}
                                >
                                  {student.studentName?.substring(0, 2)?.toUpperCase() || "ST"}
                                </div>
                                <div>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-start"
                                    onClick={() => handleViewStudent(student)}
                                    style={{ textDecoration: "none" }}
                                    title="View student's applied jobs"
                                  >
                                    <div className="fw-semibold text-primary">{student.studentName || "N/A"}</div>
                                  </button>
                                  {/* <small className="text-muted">{student.studentEmail || "N/A"}</small> */}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className="small" style={{ fontSize: "0.855rem" }}>{student.studentEmail || "N/A"}</div>
                                <div className="small text-muted" style={{ fontSize: "0.855rem" }}>{student.studentPhoneNumber || "N/A"}</div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-info text-white">
                                {getDepartmentNameById(student.department)}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-secondary">{getBranchNameById(student.selectBranch)}</span>
                            </td>
                            <td>
                              <div className="small" title={student.technologiesLearned || "Not specified"} style={{ fontSize: "0.855rem" }}>
                                {student.technologiesLearned
                                  ? student.technologiesLearned.length > 20
                                    ? student.technologiesLearned.substring(0, 20) + "..."
                                    : student.technologiesLearned
                                  : "Not specified"}
                              </div>
                            </td>
                            <td>
                              <div className="small" style={{ fontSize: "0.855rem" }}>{student.workExperience || "No experience"}</div>
                            </td>
                            <td>
                              <div className="small">{formatDate(student.createdAt)}</div>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() => handleCall(student.studentPhoneNumber)}
                                  className="p-1"
                                  title="Call"
                                >
                                  <Phone size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {appliedStudents.length > 5 && (
                      <small className="text-muted">
                        Total: {appliedStudents.length} applications{" "}
                        {searchTerm && `(${filteredStudents.length} shown)`}
                      </small>
                    )}
                  </div>
                ) : appliedStudents.length === 0 && !loading ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "60px 20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "300px",
                    }}
                  >
                    <div className="mb-3">
                      <User size={48} className="text-muted" />
                    </div>
                    <h6 className="text-muted">No Applications Yet</h6>
                    <p className="text-muted">No students have applied for this job profile yet.</p>
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "60px 20px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "300px",
                    }}
                  >
                    <div className="mb-3">
                      <Search size={48} className="text-muted" />
                    </div>
                    <h6 className="text-muted">No Results Found</h6>
                    <p className="text-muted">No students match your search criteria.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </Modal.Body>
      </div>

      <style>{`
        .fixed-size-modal {
          max-width: 95vw !important;
          width: 95vw !important;
        }

        .fixed-size-modal .modal-content {
          height: 95vh !important;
          max-height: 95vh !important;
          display: flex !important;
          flex-direction: column !important;
        }
      `}</style>

      {showStudentPopup && selectedStudentForPopup && (
        <StudentPopupView
          key={selectedStudentForPopup?.id || selectedStudentForPopup?.studentId || "student-popup"}
          student={selectedStudentForPopup}
          isVisible={showStudentPopup}
          onClose={() => {
            setShowStudentPopup(false)
            setSelectedStudentForPopup(null)
          }}
          onCloseAll={handleCloseAll} // Pass the close all function
          onUpdate={() => {}}
          users={users}
          contacts={contacts}
          courses={courses}
          departments={departments}
          branches={branches}
          batches={batches}
          jobProfiles={jobProfiles}
          defaultActiveTab="applied-jobs"
        />
      )}
    </Modal>
  )
}

export default AppliedStudentsView
